import { Pipe, PipeTransform } from '@angular/core';

type Unit = 'bytes' | 'KB' | 'MB' | 'GB' | 'TB' | 'PB';
type UnitPrecisionMap = {
    [u in Unit]: number;
};

const defaultPrecisionMap: UnitPrecisionMap = {
    bytes: 0,
    KB: 0,
    MB: 1,
    GB: 1,
    TB: 2,
    PB: 2,
};
/**
 * Converts a byte count into a readable size string using the largest
 * appropriate unit (bytes, KB, MB, GB, TB, PB). You can control decimal
 * precision with a single number or with a per–unit precision map.
 * Invalid or infinite input returns '?'.
 *
 * Dependencies:
 * - Standalone Angular pipe. No services or tokens are injected.
 *
 * Signature:
 * `transform(bytes?: number, precision?: number | UnitPrecisionMap): string`
 *
 * Parameters:
 * - `bytes`: Size in bytes. If not a finite number, the pipe returns `'?'`.
 * - `precision`: Either a single number applied to all units, or a map
 *   with per–unit precision. Defaults to an internal map with sensible values.
 *
 * Examples (Angular v20+):
 * ```html
 * <!-- Basic usage: cache with @let, guard if needed -->
 * @let readable = (file.size | fileSize);
 * {{ readable }}
 *
 * <!-- Custom single precision for all units -->
 * @let readable2 = (file.size | fileSize:2);
 * {{ readable2 }}
 *
 * <!-- Per–unit precision (define `precisionMap` in your component) -->
 * @let readable3 = (file.size | fileSize:precisionMap);
 * {{ readable3 }}
 *
 * <!-- List rendering with track and fallback for unknown sizes -->
 * @for (f of files; track f.id) {
 *   @let label = (f.size | fileSize);
 *   <div>{{ f.name }} — {{ label || '?' }}</div>
 * }
 *
 * <!-- Defensive: invalid input returns '?' -->
 * @let unknown = ('not-a-number' as any | fileSize);
 * {{ unknown }}
 * ```
 */
@Pipe({ name: 'fileSize', standalone: true })
export class FileSizePipe implements PipeTransform {
    private readonly units: Unit[] = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

    /**
     * Convert bytes to a human-readable size with unit.
     *
     * @param bytes - Input size in bytes.
     * @param precision - Global decimal places or per–unit precision map.
     * @returns Formatted string like `"1.2 MB"` or `'?'` for invalid input.
     */
    transform(
        bytes: number = 0,
        precision: number | UnitPrecisionMap = defaultPrecisionMap,
    ): string {
        if (isNaN(parseFloat(String(bytes))) || !isFinite(bytes)) return '?';

        let unitIndex = 0;

        while (bytes >= 1024) {
            bytes /= 1024;
            unitIndex++;
        }

        const unit = this.units[unitIndex];

        if (typeof precision === 'number') {
            return `${bytes.toFixed(+precision)} ${unit}`;
        }
        return `${bytes.toFixed(precision[unit])} ${unit}`;
    }
}
